import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, DollarSign, Calendar, FileText, CheckCircle, Clock, AlertCircle, Download, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [pdfDownloads, setPdfDownloads] = useState({});

  useEffect(() => {
    loadInvoices();
  }, [statusFilter]);

  const loadInvoices = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'All') {
        params.status = statusFilter;
      }
      const response = await axios.get(`${API}/invoices`, { params });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesSearch =
      invoice.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleMarkAsPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, {
        status: 'Paid',
        amount_paid: invoices.find((i) => i.id === invoiceId).total,
      });
      toast.success('Invoice marked as paid!');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleAddPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/payments`, {
        amount: parseFloat(paymentAmount),
        payment_method: paymentMethod,
        notes: paymentNotes,
      });
      toast.success('Payment recorded successfully!');
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentNotes('');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      toast.info('Generating PDF...');
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in again');
        return;
      }
      
      // Create authenticated PDF URL
      const pdfUrl = `${API}/invoices/${invoiceId}/pdf?token=${encodeURIComponent(token)}&timestamp=${Date.now()}`;
      
      // Try to open in new tab
      const newTab = window.open(pdfUrl, '_blank');
      
      if (!newTab) {
        // If popup blocked, show the download link
        toast.warn('Popup blocked! Click the PDF link below.');
      } else {
        toast.success('PDF opened in new tab! You can save it from there.');
      }
      
      // Store URL for download link
      setPdfDownloads(prev => ({
        ...prev,
        [invoiceId]: { url: pdfUrl, filename: `invoice_${invoiceNumber}.pdf` }
      }));
      
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleEmailInvoice = async (invoiceId, clientEmail) => {
    if (!clientEmail) {
      toast.error('Client email address not found');
      return;
    }

    try {
      toast.info('Sending email...');
      await axios.post(`${API}/invoices/${invoiceId}/email`);
      toast.success(`Invoice emailed successfully to ${clientEmail}`);
    } catch (error) {
      console.error('Email error:', error);
      if (error.response?.status === 400) {
        toast.error('Email not configured. Please set up SMTP in Company Settings.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to send email');
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Draft: { bg: 'bg-gray-500/10', text: 'text-gray-500', border: 'border-gray-500/20', icon: FileText },
      Sent: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/20', icon: Clock },
      'Partially Paid': { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/20', icon: AlertCircle },
      Paid: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/20', icon: CheckCircle },
      Overdue: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/20', icon: AlertCircle },
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-mono uppercase tracking-wide border ${config.bg} ${config.text} ${config.border}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const stats = {
    total: invoices.length,
    paid: invoices.filter((i) => i.status === 'Paid').length,
    unpaid: invoices.filter((i) => i.status !== 'Paid').length,
    totalRevenue: invoices.reduce((sum, i) => sum + (i.status === 'Paid' ? i.total : 0), 0),
    outstanding: invoices.reduce((sum, i) => sum + (i.status !== 'Paid' ? i.balance_due : 0), 0),
  };

  return (
    <div className="p-8" data-testid="invoice-manager-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2" data-testid="invoice-manager-title">
          INVOICE MANAGER
        </h1>
        <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
          Track payments and invoice status
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Total Invoices</p>
                <p className="text-2xl font-bold font-mono">{stats.total}</p>
              </div>
              <FileText className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Paid</p>
                <p className="text-2xl font-bold font-mono text-green-500">{stats.paid}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Unpaid</p>
                <p className="text-2xl font-bold font-mono text-red-500">{stats.unpaid}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold font-mono text-green-500">${stats.totalRevenue.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-mono uppercase text-muted-foreground">Outstanding</p>
                <p className="text-xl font-bold font-mono text-yellow-500">${stats.outstanding.toFixed(2)}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="border-border mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by client name or invoice number..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 font-mono"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {['All', 'Draft', 'Sent', 'Partially Paid', 'Paid', 'Overdue'].map((status) => (
                <Button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  variant={statusFilter === status ? 'default' : 'outline'}
                  size="sm"
                  className="font-mono uppercase text-xs"
                  data-testid={`filter-${status.toLowerCase().replace(' ', '-')}`}
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invoices List */}
      {loading ? (
        <Card className="border-border">
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">Loading invoices...</p>
          </CardContent>
        </Card>
      ) : filteredInvoices.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">No invoices found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInvoices.map((invoice) => (
            <Card key={invoice.id} className="border-border hover:border-primary transition-colors" data-testid={`invoice-${invoice.id}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold font-mono text-lg text-primary">{invoice.invoice_number}</span>
                      {getStatusBadge(invoice.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Client</p>
                        <p className="font-semibold">{invoice.client_name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Invoice Date</p>
                        <p className="font-mono">{new Date(invoice.invoice_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Due Date</p>
                        <p className="font-mono">{new Date(invoice.due_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase text-muted-foreground">Terms</p>
                        <p className="font-mono">{invoice.payment_terms}</p>
                      </div>
                    </div>
                    {invoice.po_number && (
                      <div className="mt-2">
                        <p className="text-xs font-mono uppercase text-muted-foreground">PO Number</p>
                        <p className="font-mono text-sm">{invoice.po_number}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-6 ml-6">
                    <div className="text-right">
                      <p className="text-xs font-mono uppercase text-muted-foreground">Total</p>
                      <p className="text-2xl font-mono font-bold">${invoice.total.toFixed(2)}</p>
                      {invoice.balance_due > 0 && (
                        <p className="text-sm font-mono text-yellow-500">Balance: ${invoice.balance_due.toFixed(2)}</p>
                      )}
                      {invoice.amount_paid > 0 && (
                        <p className="text-sm font-mono text-green-500">Paid: ${invoice.amount_paid.toFixed(2)}</p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {invoice.status !== 'Paid' && (
                        <>
                          <Button
                            onClick={() => {
                              setSelectedInvoice(invoice);
                              setShowPaymentModal(true);
                            }}
                            size="sm"
                            className="font-mono uppercase text-xs bg-green-600 hover:bg-green-700"
                            data-testid={`add-payment-${invoice.id}`}
                          >
                            Add Payment
                          </Button>
                          <Button
                            onClick={() => handleMarkAsPaid(invoice.id)}
                            size="sm"
                            variant="outline"
                            className="font-mono uppercase text-xs"
                            data-testid={`mark-paid-${invoice.id}`}
                          >
                            Mark as Paid
                          </Button>
                        </>
                      )}
                      <Button
                        onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                        size="sm"
                        variant="outline"
                        className="font-mono uppercase text-xs"
                        data-testid={`download-pdf-${invoice.id}`}
                      >
                        <Download size={14} className="mr-1" />
                        View PDF
                      </Button>
                      
                      <Button
                        onClick={() => handleEmailInvoice(invoice.id, invoice.client_email)}
                        size="sm"
                        variant="outline"
                        className="font-mono uppercase text-xs"
                        data-testid={`email-invoice-${invoice.id}`}
                      >
                        <Mail size={14} className="mr-1" />
                        Email Client
                      </Button>
                      
                      {pdfDownloads[invoice.id] && (
                        <a
                          href={pdfDownloads[invoice.id].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-sm font-mono uppercase text-xs transition-colors"
                          data-testid={`pdf-link-${invoice.id}`}
                        >
                          <Download size={14} />
                          Open PDF
                        </a>
                      )}
                      
                      <Button
                        size="sm"
                        variant="ghost"
                        className="font-mono uppercase text-xs"
                        data-testid={`view-${invoice.id}`}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase">
                Record Payment - {selectedInvoice.invoice_number}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-mono text-muted-foreground mb-2">
                  Balance Due: <span className="text-foreground font-bold">${selectedInvoice.balance_due.toFixed(2)}</span>
                </p>
              </div>
              <div>
                <Label htmlFor="payment-amount" className="uppercase text-xs tracking-wide">Payment Amount</Label>
                <Input
                  id="payment-amount"
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 font-mono"
                  data-testid="payment-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="payment-method" className="uppercase text-xs tracking-wide">Payment Method</Label>
                <Input
                  id="payment-method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  placeholder="Cash, Check, Transfer, etc."
                  className="mt-1"
                  data-testid="payment-method-input"
                />
              </div>
              <div>
                <Label htmlFor="payment-notes" className="uppercase text-xs tracking-wide">Notes (Optional)</Label>
                <Input
                  id="payment-notes"
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="Payment notes..."
                  className="mt-1"
                  data-testid="payment-notes-input"
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={handleAddPayment}
                  className="flex-1 font-mono uppercase"
                  data-testid="submit-payment"
                >
                  Record Payment
                </Button>
                <Button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentAmount('');
                    setPaymentMethod('');
                    setPaymentNotes('');
                  }}
                  variant="outline"
                  className="flex-1 font-mono uppercase"
                  data-testid="cancel-payment"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InvoiceManager;
